import React from "react";
import { useFetch } from "@/lib/api";
import { PageHeader, Loading, StatCard, SectionTitle } from "@/components/common";
import { fmtNum } from "@/lib/format";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line,
} from "recharts";
import { History } from "lucide-react";

export default function HistoricalAnalytics() {
  const { data, loading } = useFetch("/historical/analytics", []);
  if (loading || !data) return <Loading />;
  const h = data.data;

  return (
    <div className="fade-in">
      <PageHeader icon={History} title="Historical Analytics" subtitle="Macro intelligence on Solana launches from stored snapshots. HISTORICAL DATA — NOT A PREDICTION." />

      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Median Time to Peak" value={`${h.median_time_to_peak_min}m`} />
        <StatCard label="Liquidity Retention" value={`${h.liquidity_retention_pct}%`} />
        <StatCard label="Median Launch Liq" value={`$${fmtNum(h.median_launch_liquidity)}`} />
        <StatCard label="Sample Size" value={fmtNum(h.sample_size)} sub={h.date_range} />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="pnut-card p-4">
          <SectionTitle>Milestone Achievement Rates</SectionTitle>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={h.milestone_stats}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="milestone" tick={{ fill: "#64748b", fontSize: 11 }} />
              <YAxis tick={{ fill: "#64748b", fontSize: 10 }} unit="%" />
              <Tooltip contentStyle={{ background: "#131822", border: "1px solid #334155", borderRadius: 8 }} />
              <Bar dataKey="rate_pct" fill="#e6a15c" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-2 grid grid-cols-5 gap-1 text-center">
            {h.milestone_stats.map((m) => (
              <div key={m.milestone} className="text-[10px] text-slate-500">{fmtNum(m.count)} tokens</div>
            ))}
          </div>
        </div>

        <div className="pnut-card p-4">
          <SectionTitle>Survival Rate by Window</SectionTitle>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={h.survival_rates}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="window" tick={{ fill: "#64748b", fontSize: 11 }} />
              <YAxis tick={{ fill: "#64748b", fontSize: 10 }} unit="%" />
              <Tooltip contentStyle={{ background: "#131822", border: "1px solid #334155", borderRadius: 8 }} />
              <Line type="monotone" dataKey="alive_pct" stroke="#10b981" strokeWidth={2.5} dot={{ fill: "#10b981" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="pnut-card p-4 lg:col-span-2">
          <SectionTitle>Reached $1M+ by Momentum Bucket</SectionTitle>
          <p className="mb-3 text-[11px] text-slate-500">Historical correlation between momentum at discovery and later milestones. Correlation is not causation, and past outcomes do not predict future results.</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={h.outcome_by_momentum}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="bucket" tick={{ fill: "#64748b", fontSize: 11 }} />
              <YAxis tick={{ fill: "#64748b", fontSize: 10 }} unit="%" />
              <Tooltip contentStyle={{ background: "#131822", border: "1px solid #334155", borderRadius: 8 }} />
              <Bar dataKey="reached_1m_pct" fill="#06b6d4" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
